import { useState, useEffect } from 'react';
import { 
  FiMapPin, FiPhone, FiMail, FiSend, FiChevronRight, 
  FiFacebook, FiTwitter, FiInstagram, FiLinkedin, FiYoutube 
} from 'react-icons/fi';
import { FaHeart, FaAward, FaBuilding } from 'react-icons/fa';
import { GiClockwork, GiSparkles } from 'react-icons/gi';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About Us', href: '#about' },
    { name: 'Projects', href: '#projects' },
    { name: 'Why Us', href: '#why-us' },
    { name: 'Contact', href: '#contact' },
  ];

  const ourPresence = [
    'Prayagraj', 'Lucknow', 'Noida',
    'Gurgaon', 'Chandigarh', 'Delhi',
  ];

  const socialLinks = [
    { icon: FiFacebook, href: '#', color: 'hover:bg-[#1877f2]' },
    { icon: FiTwitter, href: '#', color: 'hover:bg-[#1da1f2]' },
    { icon: FiInstagram, href: '#', color: 'hover:bg-gradient-to-r from-[#833ab4] via-[#fd1d1d] to-[#fcb045]' },
    { icon: FiLinkedin, href: '#', color: 'hover:bg-[#0077b5]' },
    { icon: FiYoutube, href: '#', color: 'hover:bg-[#ff0000]' },
  ];

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes footerFadeUp {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes footerFloat {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-15px); }
      }
      @keyframes footerSlideIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes footerPulse {
        0%, 100% { opacity: 0.15; transform: scale(1); }
        50% { opacity: 0.3; transform: scale(1.05); }
      }
      .animate-footer-fade-up { animation: footerFadeUp 0.6s ease-out forwards; }
      .animate-footer-float { animation: footerFloat linear infinite; }
      .animate-footer-slide-in { animation: footerSlideIn 0.3s ease-out forwards; }
      .animate-footer-pulse { animation: footerPulse 4s ease-in-out infinite; }
      .delay-100 { animation-delay: 0.1s; }
      .delay-200 { animation-delay: 0.2s; }
      .delay-300 { animation-delay: 0.3s; }
      .delay-400 { animation-delay: 0.4s; }
      .font-serif { font-family: 'Cormorant Garamond', serif; }
      .font-montserrat { font-family: 'Montserrat', sans-serif; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <footer className="relative bg-navy pt-12 sm:pt-16 pb-6 sm:pb-8 overflow-hidden">
      {/* Ambient Background - Matching Hero Section */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/95 to-navy"></div>
        
        {/* Golden orb glows */}
        <div className="absolute top-0 left-0 w-[18rem] h-[18rem] sm:w-[25rem] sm:h-[25rem] bg-gold/5 rounded-full blur-3xl animate-footer-pulse"></div>
        <div className="absolute bottom-0 right-0 w-[15rem] h-[15rem] sm:w-[20rem] sm:h-[20rem] bg-amber-500/5 rounded-full blur-3xl animate-footer-pulse" style={{ animationDelay: '2s' }}></div>
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-gold/15 rounded-full animate-footer-float"
            style={{
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${Math.random() * 10 + 8}s`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-8 lg:px-12 z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-12 mb-10 sm:mb-12">
          {/* Brand Section */}
          <div className="space-y-3 sm:space-y-4 animate-footer-fade-up">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gold/20 rounded-xl blur-md"></div>
                <div className="relative w-10 h-10 sm:w-12 sm:h-12 border border-gold flex items-center justify-center z-10">
                  <div className="absolute inset-1 border border-gold/30"></div>
                  <span className="font-serif text-gold text-xl sm:text-2xl font-light">P</span>
                </div>
              </div>
              <div>
                <div className="font-montserrat text-white text-xs sm:text-sm tracking-[0.3em] uppercase leading-tight">
                  PURVANCHAL
                </div>
                <div className="font-serif text-gold text-[8px] sm:text-[9px] tracking-[0.25em] uppercase mt-0.5">
                  Global Ventures
                </div>
              </div>
            </div>
            <p className="text-gray-300 text-[11px] sm:text-xs leading-relaxed">
              Trusted real estate company with 15+ years of excellence, delivering premium property solutions across North India with transparency and reliability.
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-2 pt-2">
              <div className="flex items-center gap-1 bg-white/5 rounded-full px-2 sm:px-3 py-1">
                <FaAward size={10} className="text-gold sm:text-xs" />
                <span className="text-[9px] sm:text-[10px] text-gray-300">ISO Certified</span>
              </div>
              <div className="flex items-center gap-1 bg-white/5 rounded-full px-2 sm:px-3 py-1">
                <FaBuilding size={10} className="text-gold sm:text-xs" />
                <span className="text-[9px] sm:text-[10px] text-gray-300">RERA Registered</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="animate-footer-fade-up delay-100">
            <h4 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-5 flex items-center gap-2 font-serif">
              <div className="w-6 sm:w-8 h-px bg-gold"></div>
              Quick Links
            </h4>
            <ul className="space-y-2 sm:space-y-3">
              {quickLinks.map((link, idx) => (
                <li key={idx}>
                  <a 
                    href={link.href} 
                    className="group flex items-center gap-2 text-gray-300 hover:text-gold transition-all duration-300 text-[11px] sm:text-xs"
                  >
                    <FiChevronRight size={10} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0 -translate-x-2 transition-all" />
                    <span className="group-hover:translate-x-1 transition-transform">{link.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Presence */}
          <div className="animate-footer-fade-up delay-200">
            <h4 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-5 flex items-center gap-2 font-serif">
              <div className="w-6 sm:w-8 h-px bg-gold"></div>
              Our Presence
            </h4>
            <ul className="grid grid-cols-2 gap-2">
              {ourPresence.map((city, idx) => (
                <li key={idx} className="flex items-center gap-2 text-gray-300 text-[11px] sm:text-xs group cursor-pointer">
                  <FiMapPin size={9} className="text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                  <span className="group-hover:text-gold transition-colors">{city}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Section */}
          <div className="animate-footer-fade-up delay-300">
            <h4 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-5 flex items-center gap-2 font-serif">
              <div className="w-6 sm:w-8 h-px bg-gold"></div>
              Stay Updated
            </h4>
            <p className="text-gray-300 text-[11px] sm:text-xs mb-3 sm:mb-4 leading-relaxed">
              Subscribe to receive updates about new projects, exclusive offers, and upcoming events.
            </p>
            
            <form onSubmit={handleSubscribe} className="space-y-2 sm:space-y-3">
              <div className="relative">
                <input 
                  type="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="w-full px-3 sm:px-4 py-2 bg-white/5 border border-white/15 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-all text-[11px] sm:text-xs"
                />
              </div>
              <button 
                type="submit"
                className="group relative w-full bg-gold text-navy font-semibold py-2 rounded-lg hover:bg-gold-dark transition-all duration-300 transform hover:scale-[1.02] overflow-hidden text-[11px] sm:text-xs tracking-wide"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Subscribe
                  <FiSend size={12} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </form>
            
            {isSubscribed && (
              <div className="mt-2 p-2 bg-emerald-500/20 border border-emerald-500/50 rounded-lg animate-footer-slide-in">
                <p className="text-emerald-300 text-[10px] sm:text-xs text-center">Thanks for subscribing!</p>
              </div>
            )}
          </div>
        </div>

        {/* Contact Info Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 py-5 sm:py-6 border-t border-white/10 border-b border-white/10 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 group cursor-pointer">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors flex-shrink-0">
              <FiMapPin size={14} className="text-gold sm:text-base" />
            </div>
            <div>
              <p className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-wider">Head Office</p>
              <p className="text-[10px] sm:text-xs text-gray-300">Champa Dubey Bhawan, Civil Lines, Prayagraj</p>
            </div>
          </div>
          <div className="flex items-center gap-3 group cursor-pointer">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors flex-shrink-0">
              <FiPhone size={14} className="text-gold sm:text-base" />
            </div>
            <div>
              <p className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-wider">Call Us</p>
              <p className="text-[10px] sm:text-xs text-gray-300">+91 9336525214</p>
            </div>
          </div>
          <div className="flex items-center gap-3 group cursor-pointer sm:col-span-2 lg:col-span-1">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors flex-shrink-0">
              <GiClockwork size={14} className="text-gold sm:text-base" />
            </div>
            <div>
              <p className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-wider">Office Hours</p>
              <p className="text-[10px] sm:text-xs text-gray-300">Mon-Sat: 10AM-7PM | Sun: By Appointment</p>
            </div>
          </div>
        </div>

        {/* Social Links & Copyright */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex gap-2 sm:gap-3">
            {socialLinks.map((social, idx) => (
              <a
                key={idx}
                href={social.href}
                className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/5 flex items-center justify-center hover:scale-110 transition-all duration-300 ${social.color}`}
              >
                <social.icon size={13} className="text-gray-300 sm:text-sm" />
              </a>
            ))}
          </div>
          
          <div className="text-center text-gray-400 text-[9px] sm:text-[10px] md:text-xs">
            <p>&copy; 2024 Purvanchal Global Ventures. All rights reserved. | 
              <a href="#" className="hover:text-gold transition-colors ml-1">Privacy Policy</a> | 
              <a href="#" className="hover:text-gold transition-colors ml-1">Terms of Use</a>
            </p>
          </div>
          
          <div className="flex items-center gap-1 text-gray-500 text-[9px] sm:text-[10px]">
            <FaHeart size={9} className="text-gold sm:text-[10px]" />
            <span>Crafted with Excellence</span>
          </div>
        </div>

        {/* Bottom Decorative Line */}
        <div className="mt-6 sm:mt-8 pt-4 sm:pt-6">
          <div className="flex justify-center">
            <div className="w-16 sm:w-20 h-px bg-gold/30"></div>
          </div>
          <p className="text-center text-gray-600 text-[7px] sm:text-[8px] md:text-[9px] mt-3 sm:mt-4 tracking-wider uppercase">
            Building Dreams, Creating Landmarks Since 2009
          </p>
        </div>
      </div>

      {/* Back to Top Button */}
      {showScrollTop && (
        <button 
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-gold text-navy shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center z-50 group animate-footer-fade-up"
        >
          <FiChevronRight size={16} className="rotate-[-90deg] group-hover:translate-y-[-2px] transition-transform sm:text-lg" />
        </button>
      )}
    </footer>
  );
};

export default Footer;